import React, { useState, useRef, useEffect } from 'react';
import { caseService } from '../../services/api';
import type { ForensicExtractedAddress } from '../../services/api';
import { toast } from 'sonner';
import { Loader2, UploadCloud, CheckCircle2, ChevronRight, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FileDropzone } from '../ui/FileDropzone';
import { ForensicPartialBanner, forensicNameError, HOSTED_FORENSIC_NOTE } from './forensicUpload';

const FORENSIC_UPLOAD_MAX_MB = Number(import.meta.env.VITE_FORENSIC_UPLOAD_MAX_MB || 250);
const LOCAL_FORENSIC_APP_URL = String(import.meta.env.VITE_FORENSIC_LOCAL_APP_URL || '').trim();

// Offline extractor output — a small signed NDJSON manifest carried back from a
// lab where the raw UFDR/E01 could not be copied out. Recognised here so the
// same modal handles both a raw container and its offline-produced manifest.
const isManifestFile = (name: string) =>
    name.toLowerCase().endsWith('.ndjson') || name.toLowerCase().endsWith('.manifest.ndjson');

export interface ForensicImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    preselectedCaseId?: string; // If opened from a specific case
}

// One directory in the relevance-annotated planner tree (shared engine with the
// offline tool). ⭐ = scan-first (high value), 🚫 = auto-skipped OS/noise.
interface FTNode {
    name: string; path: string; relevance: string; reason: string;
    file_count: number; images: number; databases: number; archives: number;
    note?: string; truncated?: boolean; children: FTNode[];
}

function ForensicTreeRow({ node, depth, selected, onToggle }: {
    node: FTNode; depth: number; selected: Set<string>; onToggle: (p: string) => void;
}) {
    const [open, setOpen] = useState(node.relevance === 'high' || depth < 1);
    const hasKids = !!(node.children && node.children.length);
    const badge = node.relevance === 'high' ? '⭐' : node.relevance === 'skip' ? '🚫' : '•';
    return (
        <div>
            <div className="flex items-center gap-1 whitespace-nowrap text-[11px] leading-5" style={{ paddingLeft: depth * 12 }}>
                <span className="w-3 cursor-pointer text-muted-foreground select-none" onClick={() => hasKids && setOpen(o => !o)}>
                    {hasKids ? (open ? '▾' : '▸') : ''}
                </span>
                {node.path
                    ? <input type="checkbox" className="w-3 h-3" checked={selected.has(node.path)} onChange={() => onToggle(node.path)} />
                    : <span className="w-3" />}
                <span>{badge}</span>
                <span className="text-foreground/90 font-mono">{(node.name || '/')}/</span>
                <span className="text-muted-foreground">
                    {(node.file_count ?? 0).toLocaleString()}f{node.images ? ` ${node.images}img` : ''}{node.databases ? ` ${node.databases}db` : ''}
                </span>
                {node.reason && <span className="text-muted-foreground italic">— {node.reason}</span>}
                {node.note && <span className="text-amber-400">[{node.note}]</span>}
            </div>
            {hasKids && open && (
                <div>{node.children.map((c, i) => (
                    <ForensicTreeRow key={i} node={c} depth={depth + 1} selected={selected} onToggle={onToggle} />
                ))}</div>
            )}
        </div>
    );
}

function collectHighPaths(node: FTNode, out: string[]) {
    if (node.relevance === 'high' && node.path) out.push(node.path);
    (node.children || []).forEach(c => collectHighPaths(c, out));
}

// Rows from a server that pre-dates the tiering carry no ``confidence``: treat as verified.
const isWeakAddress = (a: { confidence?: string }) => a.confidence === 'weak';
const verifiedAddressSet = (rows: ForensicExtractedAddress[]) =>
    new Set(rows.filter(r => !isWeakAddress(r)).map(r => r.address));

export function ForensicImportModal({ isOpen, onClose, preselectedCaseId }: ForensicImportModalProps) {
    const navigate = useNavigate();
    const [file, setFile] = useState<File | null>(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [oversizeInfo, setOversizeInfo] = useState<{ fileName: string; sizeMB: number; maxMB: number } | null>(null);

    // Results state
    const [parseResults, setParseResults] = useState<ForensicExtractedAddress[]>([]);
    // Weak rows (structural match only) live in a collapsed section and are never pre-selected.
    const [showUnverified, setShowUnverified] = useState(false);
    const [selectedAddresses, setSelectedAddresses] = useState<Set<string>>(new Set());
    // Set when the uploaded file was an offline NDJSON manifest — carries the
    // server's ed25519 signature verdict for the results header badge.
    const [manifestSig, setManifestSig] = useState<{ verified: boolean; status: string } | null>(null);

    // Phase-3/5 hardening surface: inventory (dry-run) → plan → streamed extract.
    const [inventory, setInventory] = useState<any | null>(null);
    const [isInventorying, setIsInventorying] = useState(false);
    // Relevance-annotated folder tree + guidance (on-demand "Browse folders").
    const [tree, setTree] = useState<any | null>(null);
    const [isTreeLoading, setIsTreeLoading] = useState(false);
    const [treeSel, setTreeSel] = useState<Set<string>>(new Set());
    const [ocrEnabled, setOcrEnabled] = useState(true);
    const [ocrMaxImages, setOcrMaxImages] = useState<string>(''); // '' = no cap
    // Detection catalog (what to look for) + the ids the analyst has deselected.
    type Detector = { id: string; label: string; description: string; kind: string; category: string; gateable: boolean };
    const [catalog, setCatalog] = useState<Detector[]>([]);
    const [disabledDetectors, setDisabledDetectors] = useState<Set<string>>(new Set());
    const [showDetectors, setShowDetectors] = useState(false);
    const [progress, setProgress] = useState<{ filesDone: number; artifacts: number; elapsed: number; stopped?: string } | null>(null);
    const [txHashes, setTxHashes] = useState<{ value: string; chain_candidates: string[]; source: any }[]>([]);
    const [stages, setStages] = useState<any[]>([]);
    const [partialInfo, setPartialInfo] = useState<{ partial: boolean; reason?: string | null } | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [resultTab, setResultTab] = useState<'addresses' | 'txhashes' | 'audit'>('addresses');
    const cancelStreamRef = useRef<null | (() => void)>(null);

    // Case mapping state
    const [cases, setCases] = useState<any[]>([]);
    const [selectedCaseId, setSelectedCaseId] = useState<string>(preselectedCaseId || '');
    const [newCaseTitle, setNewCaseTitle] = useState('');
    const [importMode, setImportMode] = useState<'existing' | 'new'>('existing');

    const emitOversizeTelemetry = (params: { fileName: string; fileSizeMB: number; source: 'client_preflight' | 'server_413' }) => {
        const extension = params.fileName.includes('.') ? `.${params.fileName.split('.').pop()?.toLowerCase()}` : undefined;
        void caseService.trackForensicTelemetry({
            event_name: 'forensic_upload_blocked_oversize',
            file_name: params.fileName,
            file_size_mb: Number(params.fileSizeMB.toFixed(2)),
            max_allowed_mb: FORENSIC_UPLOAD_MAX_MB,
            extension,
            source: params.source,
        }).catch((err) => {
            console.debug('forensic telemetry emit failed', err);
        });
    };

    // Load cases once we have parsed results to map them to
    const loadCasesForMapping = async () => {
        try {
            const data = await caseService.getAll();
            setCases(data);
            if (!selectedCaseId && data.length > 0) {
                setSelectedCaseId(data[0].id);
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to fetch existing cases.");
        }
    };

    const processForensicFile = (selected: File) => {
            const name = selected.name.toLowerCase();
            const nameErr = forensicNameError(selected.name);
            setFileError(nameErr);
            if (nameErr) {
                toast.error(nameErr);
                setFile(null);
                return;
            }
            setPartialInfo(null);
            const manifest = isManifestFile(name);
            if (!manifest && !name.endsWith('.ufdr') && !name.endsWith('.e01')) {
                toast.error("File must be a Cellebrite .ufdr, EnCase .e01, or an offline .manifest.ndjson");
                return;
            }

            // A manifest is small and pre-extracted — the cloud size cap and
            // oversize telemetry (which exist to steer huge raw containers to the
            // offline CLI) don't apply.
            if (manifest) {
                setOversizeInfo(null);
                setFile(selected);
                setParseResults([]);
                setSelectedAddresses(new Set());
                setManifestSig(null);
                return;
            }

            const selectedSizeMB = selected.size / (1024 * 1024);
            if (selectedSizeMB > FORENSIC_UPLOAD_MAX_MB) {
                emitOversizeTelemetry({
                    fileName: selected.name,
                    fileSizeMB: selectedSizeMB,
                    source: 'client_preflight',
                });
                setFile(null);
                setParseResults([]);
                setSelectedAddresses(new Set());
                setOversizeInfo({
                    fileName: selected.name,
                    sizeMB: selectedSizeMB,
                    maxMB: FORENSIC_UPLOAD_MAX_MB,
                });
                toast.error(`File exceeds cloud upload limit (${FORENSIC_UPLOAD_MAX_MB}MB). Use Local CLI/Desktop parser.`);
                return;
            }

            setOversizeInfo(null);
            setFile(selected);
            setParseResults([]);
            setSelectedAddresses(new Set());
            setInventory(null);
            setTree(null);
            setTreeSel(new Set());
            setProgress(null);
            setTxHashes([]);
            setStages([]);
            setManifestSig(null);
            // Kick off the cheap dry-run so the analyst sees a plan + ETA before
            // committing to the expensive extraction.
            void runInventory(selected);
    };

    const runInventory = async (f: File) => {
        setIsInventorying(true);
        try {
            const inv = await caseService.forensicInventory(f);
            setInventory(inv);
        } catch (e) {
            console.debug('inventory failed (non-fatal)', e);
            setInventory(null);
        } finally {
            setIsInventorying(false);
        }
    };

    // Load the detection catalog once (shared source of truth with CLI + offline UI).
    useEffect(() => {
        let cancelled = false;
        caseService.detectionCatalog()
            .then((r: any) => { if (!cancelled) setCatalog(r?.detectors || []); })
            .catch(() => { /* non-fatal: the plan still works with defaults */ });
        return () => { cancelled = true; };
    }, []);

    const toggleDetector = (id: string) => {
        setDisabledDetectors(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Mirror of detection_catalog.resolve_selection: a group (methods /
    // artifact_types) is only restricted when the analyst left at least one of
    // its members out — so deselecting an artefact type never disables the
    // decoders that find it.
    const resolveDetectorGates = (): Record<string, any> => {
        const gate: Record<string, any> = {};
        const groups: Record<'method' | 'artifact_type', string> = { method: 'methods', artifact_type: 'artifact_types' };
        (['method', 'artifact_type'] as const).forEach(kind => {
            const members = catalog.filter(d => d.kind === kind && d.gateable).map(d => d.id);
            const enabled = members.filter(id => !disabledDetectors.has(id));
            if (enabled.length > 0 && enabled.length < members.length) {
                gate[groups[kind]] = enabled;   // a genuine subset → restrict
            }
        });
        return gate;
    };

    const buildOptions = (): Record<string, any> => {
        const opts: Record<string, any> = { enable_ocr: ocrEnabled };
        const cap = parseInt(ocrMaxImages, 10);
        if (!isNaN(cap) && cap > 0) opts.ocr_max_images = cap;
        // Ticked folders → scan-only globs (parents still traversed to reach them).
        if (treeSel.size > 0) {
            opts.only_globs = [...treeSel].filter(Boolean).map(p => `*/${p.toLowerCase()}/*`);
        }
        Object.assign(opts, resolveDetectorGates());
        return opts;
    };

    // On-demand folder-tree preview (a second small upload; only when the analyst
    // asks for it). Same shared engine + guidance as the offline tool.
    const handleBrowseTree = async () => {
        if (!file) return;
        setIsTreeLoading(true);
        try {
            const t = await caseService.forensicTree(file);
            setTree(t);
        } catch (e: any) {
            console.debug('tree preview failed (non-fatal)', e);
            toast.error(e?.response?.data?.detail || 'Could not read folder structure.');
        } finally {
            setIsTreeLoading(false);
        }
    };

    const toggleTreePath = (p: string) => {
        setTreeSel(prev => {
            const next = new Set(prev);
            if (next.has(p)) next.delete(p); else next.add(p);
            return next;
        });
    };
    const tickHighValue = () => {
        if (!tree?.root) return;
        const out: string[] = [];
        collectHighPaths(tree.root, out);
        setTreeSel(new Set(out));
    };

    // Shared result applier for both the streamed and blocking extraction paths.
    const applyExtractResult = (res: any) => {
        setParseResults(res.extracted_addresses || []);
        setSelectedAddresses(verifiedAddressSet(res.extracted_addresses || []));
        setShowUnverified(false);
        setTxHashes(res.tx_hashes || []);
        setStages(res.processing_stages || []);
        setPartialInfo(res.partial ? { partial: true, reason: res.partial_reason } : null);
        if ((res.extracted_addresses || []).length > 0) {
            toast.success(`Extracted ${res.extracted_addresses.length} addresses, ${(res.tx_hashes || []).length} tx hashes.`);
            loadCasesForMapping();
        } else {
            toast.warning("File parsed but no crypto addresses were detected.");
        }
    };

    // Streamed container extraction with a live progress bar; falls back to the
    // blocking parse if the stream can't start.
    const handleExtractStream = async () => {
        if (!file) return;
        setIsParsing(true);
        setProgress({ filesDone: 0, artifacts: 0, elapsed: 0 });
        try {
            const cancel = await caseService.parseForensicStream(file, buildOptions(), (evt, data) => {
                if (evt === 'file_done') {
                    setProgress({ filesDone: data.files_done || 0, artifacts: data.artifacts || 0, elapsed: data.elapsed_s || 0 });
                } else if (evt === 'budget_stop') {
                    setProgress(p => ({ ...(p || { filesDone: 0, artifacts: 0, elapsed: 0 }), stopped: data.reason }));
                } else if (evt === 'complete') {
                    setProgress(null);
                    setIsParsing(false);
                    cancelStreamRef.current = null;
                    applyExtractResult(data);
                } else if (evt === 'error') {
                    setProgress(null);
                    setIsParsing(false);
                    cancelStreamRef.current = null;
                    toast.error(data?.message || 'Extraction failed.');
                }
            });
            cancelStreamRef.current = cancel;
        } catch (e) {
            // Stream couldn't start — fall back to the blocking parse.
            console.debug('stream start failed, falling back to blocking parse', e);
            await handleParse();
        }
    };

    const handleCancelStream = () => {
        if (cancelStreamRef.current) {
            cancelStreamRef.current();
            cancelStreamRef.current = null;
        }
        setProgress(null);
        setIsParsing(false);
        toast.info('Extraction cancelled.');
    };

    const handleParse = async () => {
        if (!file) return;

        // Offline manifest re-import: the addresses are already extracted; the
        // server verifies the signature and persists evidence into the case.
        // Requires a case context (evidence must attach to a case).
        if (isManifestFile(file.name)) {
            if (!preselectedCaseId) {
                toast.error("Open an offline manifest from within a case so its evidence can be attached.");
                return;
            }
            setIsParsing(true);
            toast.info("Ingesting offline manifest and verifying signature...", { duration: 4000 });
            try {
                const res = await caseService.ingestManifest(preselectedCaseId, file, { notes: `Offline manifest: ${file.name}` });
                setParseResults(res.extracted_addresses || []);
                setSelectedAddresses(verifiedAddressSet(res.extracted_addresses || []));
                setShowUnverified(false);
                setManifestSig({ verified: !!res.signature_verified, status: res.signature_status || 'unsigned' });
                setSelectedCaseId(preselectedCaseId);
                if ((res.extracted_addresses || []).length > 0) {
                    loadCasesForMapping();
                    toast.success(`Manifest ingested — ${res.extracted_addresses.length} addresses (${res.deduplicated ? 'already on record' : 'evidence saved'}).`);
                } else {
                    toast.warning("Manifest ingested but contained no crypto addresses.");
                }
            } catch (e: any) {
                console.error("Manifest ingest failed", e);
                toast.error(e.response?.data?.detail || "Failed to ingest manifest.");
            } finally {
                setIsParsing(false);
            }
            return;
        }

        setIsParsing(true);
        toast.info("Extracting addresses... This may take several minutes for large generic archives.", { duration: 5000 });

        try {
            const res = await caseService.parseForensicFile(file);
            applyExtractResult(res);
        } catch (e: any) {
            console.error("Forensic parse failed", e);
            if (e?.response?.status === 413) {
                const sizeMB = file ? file.size / (1024 * 1024) : 0;
                emitOversizeTelemetry({
                    fileName: file?.name || 'forensic file',
                    fileSizeMB: sizeMB,
                    source: 'server_413',
                });
                setOversizeInfo({
                    fileName: file?.name || 'forensic file',
                    sizeMB,
                    maxMB: FORENSIC_UPLOAD_MAX_MB,
                });
            }
            toast.error(e.response?.data?.detail || "Failed to parse forensic extraction.");
        } finally {
            setIsParsing(false);
        }
    };

    const toggleAddress = (address: string) => {
        const next = new Set(selectedAddresses);
        if (next.has(address)) {
            next.delete(address);
        } else {
            next.add(address);
        }
        setSelectedAddresses(next);
    };

    const verifiedRows = parseResults.filter(r => !isWeakAddress(r));
    const unverifiedRows = parseResults.filter(isWeakAddress);

    const renderAddressRow = (r: ForensicExtractedAddress, i: number) => (
        <tr
            key={`${r.chain}:${r.address}:${i}`}
            onClick={() => toggleAddress(r.address)}
            className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
        >
            <td className="p-2 text-center">
                <input
                    type="checkbox"
                    checked={selectedAddresses.has(r.address)}
                    onChange={() => { }} // Handled by row click
                    className="w-4 h-4 rounded border-border/80 bg-muted text-primary focus:ring-blue-500"
                />
            </td>
            <td className="p-2 font-medium">
                <span className={`px-2 py-0.5 rounded text-[10px] ${r.chain === 'BTC' ? 'bg-orange-500/20 text-orange-400' :
                    r.chain === 'ETH' ? 'bg-primary/20 text-primary' :
                        'bg-red-500/20 text-red-400'
                    }`}>
                    {r.chain}
                </span>
            </td>
            <td className="p-2 text-xs text-muted-foreground max-w-[150px] truncate" title={r.metadata?.logical_file || r.metadata?.extraction_path}>
                {r.metadata?.logical_file || "Unknown Root DB"}
            </td>
            <td className="p-2 font-mono text-xs text-foreground/80">
                {r.address}
                {isWeakAddress(r) ? (
                    <span
                        title={r.confidence_reason || 'Structural match only; no checksum proves this is an address'}
                        className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400"
                    >
                        unverified
                    </span>
                ) : (
                    <span
                        data-testid="verified-chip"
                        title="Passed a cryptographic checksum"
                        className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-green-500/20 text-green-400"
                    >
                        verified
                    </span>
                )}
            </td>
        </tr>
    );

    const toggleAll = () => {
        if (selectedAddresses.size === parseResults.length) {
            setSelectedAddresses(new Set());
        } else {
            setSelectedAddresses(new Set(parseResults.map((a: any) => a.address)));
        }
    };

    const handleFinalImport = async (strategy: 'separate' | 'unified') => {
        if (selectedAddresses.size === 0) {
            toast.error("Please select at least one address to import.");
            return;
        }

        if (importMode === 'existing' && !selectedCaseId) {
            toast.error("Please select a case to import into.");
            return;
        }

        if (importMode === 'new' && !newCaseTitle.trim()) {
            toast.error("Please provide a name for the new case.");
            return;
        }

        setIsImporting(true);
        try {
            const payload: any = {
                addresses: parseResults
                    .filter(r => selectedAddresses.has(r.address))
                    .map(r => ({ address: r.address, chain: r.chain }))
            };

            if (importMode === 'existing') {
                payload.case_id = selectedCaseId;
            } else {
                payload.case_metadata = { title: newCaseTitle, notes: `Forensic Import: ${file?.name}` };
            }

            const importRes = await caseService.importAddresses(payload);
            const finalCaseId = importRes.case.id;

            toast.success("Addresses imported successfully!");

            if (strategy === 'unified') {
                // Open the unified workbench canvas with all parsed seeds.
                // Workbench reads `case_id` (snake_case) for case context, and `seeds` for
                // multi-chain bulk import in CHAIN:address comma-joined format.
                const urlParams = new URLSearchParams();
                urlParams.set('case_id', finalCaseId);

                const unifiedSeeds = payload.addresses.map((a: any) => `${a.chain}:${a.address}`).join(',');
                urlParams.set('seeds', unifiedSeeds);

                onClose();
                navigate(`/workbench?${urlParams.toString()}`);
            } else {
                // Separate investigations
                onClose();
                navigate(`/cases/${finalCaseId}`);
            }

        } catch (e: any) {
            console.error("Import failed", e);
            toast.error(e.response?.data?.detail || "Failed to finalize import.");
        } finally {
            setIsImporting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl rounded-xl flex flex-col max-h-[90vh] border border-border bg-card p-6 shadow-2xl">

                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-bold flex items-center gap-2 text-foreground">
                            <UploadCloud className="w-5 h-5 text-primary" />
                            Forensic Upload
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Cellebrite .ufdr{preselectedCaseId ? ', or a signed offline manifest (.manifest.ndjson)' : ''}. {HOSTED_FORENSIC_NOTE}
                        </p>
                    </div>
                </div>

                {/* Step 1: Upload & Parse */}
                {!parseResults.length && !isParsing && (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 border-2 border-dashed border-border/80 rounded-xl bg-card/50">
                        <UploadCloud className="w-12 h-12 text-muted-foreground mb-4" />
                        <h4 className="text-foreground font-medium mb-2">Select Forensic File</h4>
                        <p className="text-xs text-muted-foreground mb-6 text-center max-w-sm">
                            Extract crypto traces (BTC, ETH, TRX) deep within mobile chats and disk files.
                        </p>

                        <p className="text-[11px] text-amber-400/90 mb-4 text-center max-w-md">
                            Cloud parse limit: <strong>{FORENSIC_UPLOAD_MAX_MB}MB</strong>. For larger files, use Local CLI/Desktop parser.
                        </p>
                        {preselectedCaseId && (
                            <p className="text-xs text-muted-foreground mb-4 text-center max-w-md">
                                Sign offline manifests in CryptoScan before uploading. A valid signature is required for import.
                            </p>
                        )}

                        <div className="w-full max-w-md space-y-3">
                            <FileDropzone
                                bare
                                accept={preselectedCaseId ? '.ufdr,.e01,.ndjson' : '.ufdr,.e01'}
                                file={file}
                                onFileChange={(f) => { if (f) processForensicFile(f); else setFile(null); }}
                                title="Forensic container"
                                description={preselectedCaseId ? '.ufdr · .ndjson (hosted). .e01 is the offline build.' : '.ufdr on the hosted platform. .e01 is the offline build.'}
                            />

                            {fileError && (
                                <p role="alert" data-testid="forensic-file-error" className="text-[11px] text-red-500 leading-relaxed">
                                    {fileError}
                                </p>
                            )}
                            <ForensicPartialBanner partial={partialInfo?.partial} reason={partialInfo?.reason} />

                            {file && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={isManifestFile(file.name) ? handleParse : handleExtractStream}
                                        disabled={isInventorying}
                                        className="px-3 py-1.5 bg-primary hover:bg-brand-gold-hover text-primary-foreground rounded-md text-sm whitespace-nowrap disabled:opacity-50"
                                    >
                                        {isManifestFile(file.name) ? 'Ingest Manifest' : 'Extract Target Wallets'}
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Inventory (dry-run) preview + extraction plan — containers only */}
                        {file && !isManifestFile(file.name) && (
                            <div className="mt-4 w-full max-w-md text-left">
                                {isInventorying && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-2">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Inventorying container…
                                    </p>
                                )}
                                {inventory && (
                                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs space-y-1.5">
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                                            <span><b className="text-foreground">{inventory.total_files?.toLocaleString?.() ?? inventory.total_files}</b> files</span>
                                            <span><b className="text-foreground">{inventory.image_count}</b> images</span>
                                            <span><b className="text-foreground">{inventory.sqlite_count}</b> databases</span>
                                            <span><b className="text-foreground">{inventory.archive_count}</b> archives</span>
                                            {inventory.has_report_xml && <span className="text-green-500">report.xml ✓</span>}
                                        </div>
                                        {inventory.image_count > 0 && (
                                            <div className="text-muted-foreground">
                                                Est. OCR time: <b className="text-foreground">{Math.round((inventory.estimated_ocr_seconds || 0) / 60 * 10) / 10} min</b> for {inventory.image_count} images
                                            </div>
                                        )}
                                        {Array.isArray(inventory.encrypted_hints) && inventory.encrypted_hints.length > 0 && (
                                            <div className="text-amber-400">⚠ {inventory.encrypted_hints.length} encrypted / needs-key item(s) — will be noted, not decrypted.</div>
                                        )}
                                        {/* Plan controls */}
                                        <div className="flex items-center gap-4 pt-1 border-t border-border/40 mt-1">
                                            <label className="flex items-center gap-1.5 cursor-pointer text-foreground">
                                                <input type="checkbox" checked={ocrEnabled} onChange={e => setOcrEnabled(e.target.checked)} className="w-3.5 h-3.5" />
                                                OCR images
                                            </label>
                                            {ocrEnabled && inventory.image_count > 50 && (
                                                <label className="flex items-center gap-1.5 text-muted-foreground">
                                                    cap:
                                                    <input type="number" value={ocrMaxImages} onChange={e => setOcrMaxImages(e.target.value)}
                                                        placeholder="all" className="w-16 bg-card border border-border/60 rounded px-1 py-0.5 text-xs" />
                                                    images
                                                </label>
                                            )}
                                        </div>
                                        {/* Detections — what to look for (select/deselect). Shared catalog. */}
                                        {catalog.length > 0 && (
                                            <div className="pt-1 border-t border-border/40 mt-1">
                                                <button type="button" onClick={() => setShowDetectors(v => !v)}
                                                    className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
                                                    <ChevronRight className={`w-3 h-3 transition-transform ${showDetectors ? 'rotate-90' : ''}`} />
                                                    Detections — {catalog.filter(d => d.gateable && !disabledDetectors.has(d.id)).length}/{catalog.filter(d => d.gateable).length} on
                                                </button>
                                                {showDetectors && (
                                                    <div className="mt-1.5 space-y-1.5">
                                                        {[...new Set(catalog.map(d => d.category))].map(cat => (
                                                            <div key={cat}>
                                                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-0.5">{cat}</div>
                                                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                                                    {catalog.filter(d => d.category === cat).map(d => (
                                                                        <label key={d.id} title={d.description}
                                                                            className={`flex items-center gap-1 ${d.gateable ? 'cursor-pointer text-foreground' : 'text-muted-foreground/60'}`}>
                                                                            <input type="checkbox" className="w-3 h-3"
                                                                                disabled={!d.gateable}
                                                                                checked={d.gateable ? !disabledDetectors.has(d.id) : true}
                                                                                onChange={() => toggleDetector(d.id)} />
                                                                            {d.label}{!d.gateable && <span className="text-[9px]">(always)</span>}
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Browse folders — relevance tree + guidance (on-demand) */}
                                <div className="mt-3">
                                    {!tree && (
                                        <button
                                            onClick={handleBrowseTree}
                                            disabled={isTreeLoading}
                                            className="text-xs px-2.5 py-1 rounded border border-border/60 bg-muted/40 hover:bg-muted text-foreground disabled:opacity-50 flex items-center gap-1.5"
                                        >
                                            {isTreeLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                                            Browse folders &amp; choose what to scan
                                        </button>
                                    )}
                                    {tree && (
                                        <div className="rounded-lg border border-border/60 bg-muted/20 p-2 text-xs">
                                            {Array.isArray(tree.guidance) && tree.guidance.length > 0 && (
                                                <div className="mb-2 rounded bg-emerald-500/10 border border-emerald-500/20 p-2">
                                                    <div className="font-semibold text-emerald-300 mb-1">Guidance</div>
                                                    <ul className="list-disc pl-4 space-y-0.5 text-foreground/90">
                                                        {tree.guidance.map((g: string, i: number) => <li key={i}>{g}</li>)}
                                                    </ul>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 mb-1">
                                                <button onClick={tickHighValue} className="text-[11px] px-2 py-0.5 rounded border border-border/60 hover:bg-muted text-foreground">⭐ Tick high-value</button>
                                                <button onClick={() => setTreeSel(new Set())} className="text-[11px] px-2 py-0.5 rounded border border-border/60 hover:bg-muted text-foreground">Clear</button>
                                                <span className="text-muted-foreground">
                                                    {treeSel.size ? `scanning ${treeSel.size} folder(s)` : 'scanning everything (nothing ticked)'}
                                                </span>
                                            </div>
                                            {tree.root ? (
                                                <div className="max-h-52 overflow-auto rounded bg-card/60 border border-border/50 p-1">
                                                    <ForensicTreeRow node={tree.root} depth={0} selected={treeSel} onToggle={toggleTreePath} />
                                                </div>
                                            ) : (
                                                <div className="text-muted-foreground">{(tree.notes || []).join(' ') || 'No folder view for this source.'}</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {progress && (
                                    <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Extracting…</span>
                                            <button onClick={handleCancelStream} className="text-muted-foreground hover:text-red-400">Cancel</button>
                                        </div>
                                        <div className="text-muted-foreground">
                                            {progress.filesDone.toLocaleString()} files · {progress.artifacts.toLocaleString()} artefacts · {progress.elapsed}s
                                            {inventory?.total_files ? ` / ~${inventory.total_files.toLocaleString()} files` : ''}
                                        </div>
                                        {inventory?.total_files > 0 && (
                                            <div className="mt-1.5 h-1.5 bg-muted rounded overflow-hidden">
                                                <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, Math.round((progress.filesDone / inventory.total_files) * 100))}%` }} />
                                            </div>
                                        )}
                                        {progress.stopped && <div className="mt-1 text-amber-400">Budget stop: {progress.stopped}</div>}
                                    </div>
                                )}
                            </div>
                        )}

                        {oversizeInfo && (
                            <div className="mt-4 w-full max-w-md rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                                <p className="font-semibold mb-1">Large forensic file detected</p>
                                <p>
                                    <span className="font-mono">{oversizeInfo.fileName}</span> is {oversizeInfo.sizeMB.toFixed(1)}MB,
                                    above cloud limit {oversizeInfo.maxMB}MB.
                                </p>
                                <p className="mt-1">Use Local CLI/Desktop parser and import extracted findings only.</p>
                                {LOCAL_FORENSIC_APP_URL ? (
                                    <a
                                        href={LOCAL_FORENSIC_APP_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-block mt-2 text-primary hover:text-blue-200 underline"
                                    >
                                        Get Local CLI/Desktop Parser
                                    </a>
                                ) : (
                                    <p className="mt-2 text-foreground/80">Contact your admin for Local Parser installer/CLI package.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Loading State — shows live progress when streaming */}
                {isParsing && (
                    <div className="flex-1 flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                        <p className="text-foreground font-medium">Extracting from forensic archive…</p>
                        {progress ? (
                            <div className="mt-4 w-full max-w-sm text-center">
                                <p className="text-sm text-muted-foreground">
                                    {progress.filesDone.toLocaleString()} files · {progress.artifacts.toLocaleString()} artefacts · {progress.elapsed}s
                                </p>
                                {inventory?.total_files > 0 && (
                                    <div className="mt-2 h-2 bg-muted rounded overflow-hidden">
                                        <div className="h-full bg-primary transition-all"
                                            style={{ width: `${Math.min(100, Math.round((progress.filesDone / inventory.total_files) * 100))}%` }} />
                                    </div>
                                )}
                                {progress.stopped && <p className="mt-2 text-xs text-amber-400">Budget stop: {progress.stopped}</p>}
                                <button onClick={handleCancelStream} className="mt-3 text-xs text-muted-foreground hover:text-red-400 underline">
                                    Cancel extraction
                                </button>
                            </div>
                        ) : (
                            <p className="text-xs text-muted-foreground mt-2 text-center max-w-xs">
                                This may take several minutes as the system inspects file structures, decodes databases, and OCRs images.
                            </p>
                        )}
                    </div>
                )}

                {/* Step 2: Mapping / Results */}
                {parseResults.length > 0 && !isParsing && (
                    <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex items-center justify-between mb-3 border-b border-border pb-2">
                            <h4 className="text-foreground font-medium text-sm flex items-center gap-2">
                                Extracted Targets ({selectedAddresses.size} / {parseResults.length} selected)
                                <span className="text-xs font-normal text-muted-foreground">
                                    {verifiedRows.length} verified · {unverifiedRows.length} unverified
                                </span>
                                {manifestSig && (
                                    <span
                                        title={`Offline manifest signature: ${manifestSig.status}`}
                                        className={`px-2 py-0.5 rounded text-[10px] font-medium ${manifestSig.verified
                                            ? 'bg-green-500/20 text-green-400'
                                            : 'bg-amber-500/20 text-amber-400'}`}
                                    >
                                        {manifestSig.verified ? '✓ Signed manifest' : `Unverified (${manifestSig.status})`}
                                    </span>
                                )}
                            </h4>
                            <button onClick={toggleAll} className="text-xs text-primary hover:underline">
                                {selectedAddresses.size === parseResults.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>

                        <ForensicPartialBanner partial={partialInfo?.partial} reason={partialInfo?.reason} />

                        {/* Result tabs: addresses / tx hashes / extraction audit */}
                        <div className="flex items-center gap-1 mb-2 text-xs">
                            {([['addresses', `Addresses (${parseResults.length})`], ['txhashes', `Tx hashes (${txHashes.length})`], ['audit', `Audit (${stages.length})`]] as const).map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setResultTab(key)}
                                    className={`px-2.5 py-1 rounded ${resultTab === key ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Table of results — addresses */}
                        {resultTab === 'addresses' && (
                        <div className="flex-1 overflow-y-auto mb-4 border border-border rounded-lg bg-card">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-card border-b border-border sticky top-0">
                                    <tr>
                                        <th className="p-2 w-10 text-center"></th>
                                        <th className="p-2 text-muted-foreground font-medium">Network</th>
                                        <th className="p-2 text-muted-foreground font-medium whitespace-nowrap">Source Evidence</th>
                                        <th className="p-2 text-muted-foreground font-medium">Wallet Address</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {verifiedRows.map((r, i) => renderAddressRow(r, i))}
                                    {unverifiedRows.length > 0 && (
                                        <tr className="border-b border-border/50 bg-muted/20">
                                            <td colSpan={4} className="p-0">
                                                <button
                                                    type="button"
                                                    data-testid="unverified-toggle"
                                                    onClick={() => setShowUnverified(v => !v)}
                                                    className="w-full text-left px-3 py-2 text-xs text-amber-400 hover:bg-muted/30"
                                                >
                                                    {showUnverified ? '▾' : '▸'} Unverified — structural match only ({unverifiedRows.length})
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                    {showUnverified && unverifiedRows.map((r, i) => renderAddressRow(r, verifiedRows.length + i))}
                                </tbody>
                            </table>
                        </div>
                        )}

                        {/* Transaction hashes */}
                        {resultTab === 'txhashes' && (
                        <div className="flex-1 overflow-y-auto mb-4 border border-border rounded-lg bg-card">
                            {txHashes.length === 0 ? (
                                <p className="p-4 text-xs text-muted-foreground">No transaction hashes found.</p>
                            ) : (
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-card border-b border-border sticky top-0">
                                        <tr>
                                            <th className="p-2 text-muted-foreground font-medium">Chain</th>
                                            <th className="p-2 text-muted-foreground font-medium">Transaction Hash</th>
                                            <th className="p-2 text-muted-foreground font-medium">Source</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {txHashes.map((t, i) => (
                                            <tr key={i} className="border-b border-border/50">
                                                <td className="p-2 font-medium text-xs">{(t.chain_candidates || ['?'])[0]}</td>
                                                <td className="p-2 font-mono text-xs text-foreground/80 truncate max-w-[240px]" title={t.value}>{t.value}</td>
                                                <td className="p-2 text-xs text-muted-foreground truncate max-w-[130px]" title={t.source?.extraction_path}>{t.source?.logical_file || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        )}

                        {/* Extraction audit — the 8-method processing stages */}
                        {resultTab === 'audit' && (
                        <div className="flex-1 overflow-y-auto mb-4 border border-border rounded-lg bg-card">
                            {stages.length === 0 ? (
                                <p className="p-4 text-xs text-muted-foreground">No audit stages recorded.</p>
                            ) : (
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-card border-b border-border sticky top-0">
                                        <tr>
                                            <th className="p-2 text-muted-foreground font-medium">Method</th>
                                            <th className="p-2 text-muted-foreground font-medium text-right">Attempted</th>
                                            <th className="p-2 text-muted-foreground font-medium text-right">OK</th>
                                            <th className="p-2 text-muted-foreground font-medium text-right">Failed</th>
                                            <th className="p-2 text-muted-foreground font-medium text-right">Artefacts</th>
                                            <th className="p-2 text-muted-foreground font-medium text-right">Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stages.map((s, i) => (
                                            <tr key={i} className="border-b border-border/50">
                                                <td className="p-2 text-xs font-medium text-foreground/90">{s.method}</td>
                                                <td className="p-2 text-xs text-right tabular-nums">{s.files_attempted ?? 0}</td>
                                                <td className="p-2 text-xs text-right tabular-nums text-green-500">{s.files_succeeded ?? 0}</td>
                                                <td className="p-2 text-xs text-right tabular-nums text-red-400">{s.files_failed ?? 0}</td>
                                                <td className="p-2 text-xs text-right tabular-nums">{s.artefacts_yielded ?? 0}</td>
                                                <td className="p-2 text-xs text-right tabular-nums text-muted-foreground">{(s.duration_seconds ?? 0).toFixed?.(2) ?? s.duration_seconds}s</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        )}

                        {/* Case Mapping Form */}
                        <div className="bg-muted/40 p-4 rounded-lg border border-border/50 space-y-4">
                            {!preselectedCaseId && (
                                <div className="flex gap-4 border-b border-border/80 pb-3">
                                    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                        <input
                                            type="radio"
                                            name="case-dest"
                                            checked={importMode === 'existing'}
                                            onChange={() => setImportMode('existing')}
                                            className="text-primary bg-card border-border/80"
                                        />
                                        Associate with Existing Case
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                        <input
                                            type="radio"
                                            name="case-dest"
                                            checked={importMode === 'new'}
                                            onChange={() => setImportMode('new')}
                                            className="text-primary bg-card border-border/80"
                                        />
                                        Spin Up New Case
                                    </label>
                                </div>
                            )}

                            {importMode === 'existing' || preselectedCaseId ? (
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">Select Case</label>
                                    <select
                                        value={selectedCaseId}
                                        onChange={e => setSelectedCaseId(e.target.value)}
                                        className="w-full bg-card border border-border/80 rounded-md p-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                        disabled={!!preselectedCaseId}
                                    >
                                        <option value="" disabled>-- Select a valid case --</option>
                                        {cases.map(c => (
                                            <option key={c.id} value={c.id}>{c.title} {c.fir_number ? `(${c.fir_number})` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">New Case Title</label>
                                    <input
                                        type="text"
                                        value={newCaseTitle}
                                        onChange={e => setNewCaseTitle(e.target.value)}
                                        placeholder="e.g. Operation Ghost Protocol"
                                        className="w-full bg-card border border-border/80 rounded-md p-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Actions Footer */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-border">
                    <button
                        onClick={onClose}
                        disabled={isParsing || isImporting}
                        className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                        Close
                    </button>

                    {parseResults.length > 0 && (
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleFinalImport('separate')}
                                disabled={isImporting || selectedAddresses.size === 0}
                                className="px-4 py-2 text-sm bg-muted border border-border/80 rounded-md hover:bg-muted text-foreground transition-colors"
                                title="Creates one isolated investigation subgraph per wallet address"
                            >
                                Track Distinct Vectors
                            </button>
                            <button
                                onClick={() => handleFinalImport('unified')}
                                disabled={isImporting || selectedAddresses.size === 0}
                                className="px-4 py-2 text-sm bg-blue-600 rounded-md hover:bg-primary text-foreground transition-colors flex items-center gap-2"
                                title="Drops all wallets onto a single shared Workbench canvas to map interactions"
                            >
                                {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />}
                                Unified Graph Import
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
